import * as React from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Check, FilePen, PenLine, Receipt, Send } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { EventSalesDto, SalesPacketDto } from '@lib/dto/sales-packet.dto'
import type { SalesReadinessDto } from '@lib/dto/sales-setup.dto'
import { isAgreementPacketKind, SALES_LINE_GROUPS, SALES_PACKET_STATUS_LABELS, type SalesLineGroup } from '@lib/enums/sales-packet.enum'
import { dollarsFromCents } from '@lib/helpers/format'
import { milliToPercent } from '@lib/helpers/sales-money'
import { packetRange } from '@lib/helpers/sales-packet'
import type { ScheduleDue, ScheduleEntry } from '@lib/helpers/sales-schedule'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Select } from '@/components/ui/select'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useAuth } from '@/auth/AuthContext'
import { salesKeys } from '@/hooks/queryKeys'
import { useCountersign, useEventSales, useExtendPacket, useSalesReadiness, useSetCountersigner, useWithdrawPacket } from '@/hooks/useSales'
import { todayIso } from '@/lib/bookedDays'
import { fmt12, formatEventDate } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { InvoiceBuilder } from './InvoiceBuilder'
import { PacketBuilder } from './PacketBuilder'
import { PaymentsPanel } from './PaymentsPanel'
import { SigningFrame, type SigningOutcome } from './SigningFrame'

/** Where in the venue's settings each missing thing is fixed. */
const SETUP_LINKS: { flag: keyof Pick<SalesReadinessDto, 'priceList' | 'contractTemplate' | 'schedule' | 'business'>; section: string; label: string }[] = [
  { flag: 'priceList', section: 'price-list', label: 'Add a package with a price to the price list.' },
  { flag: 'contractTemplate', section: 'contracts', label: 'Write a contract template.' },
  { flag: 'schedule', section: 'payment-schedules', label: 'Save a payment schedule.' },
  { flag: 'business', section: 'getting-paid', label: 'Finish setting up how this venue gets paid.' },
]

const GROUP_LABELS = { package: 'Packages, pick one', add_on: 'Add-ons', included: 'Included' } as const satisfies Record<SalesLineGroup, string>

function statusTone(packet: SalesPacketDto): BadgeTone {
  if (packet.lapsed) return 'neutral'
  if (packet.status === 'open' || packet.status === 'signing') return 'info'
  if (packet.status === 'signed') return 'success'
  return 'neutral'
}

/** "due on signing", "due 30 days before", "due Mar 1, 2027" */
function dueText(due: ScheduleDue): string {
  if (due.kind === 'on_signing') return 'due on signing'
  if (due.kind === 'before_event') return `due ${String(due.days)} days before`
  return `due ${formatEventDate(due.date)}`
}

/** "Deposit 30%, due on signing" */
function paymentText(entry: ScheduleEntry): string {
  let amount = 'the rest'
  if (entry.amount.kind === 'fixed') amount = dollarsFromCents(entry.amount.cents)
  if (entry.amount.kind === 'percent') amount = `${milliToPercent(entry.amount.milli)}%`
  return `${entry.label} ${amount}, ${dueText(entry.due)}`
}

function shortDay(instant: string): string {
  return new Date(instant).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "Jun 13, 2099, 4:00 PM to 11:00 PM" */
function whenOf(packet: SalesPacketDto): string {
  const day = formatEventDate(packet.date)
  return packet.startsAt === null || packet.endsAt === null ? day : `${day}, ${fmt12(packet.startsAt)} to ${fmt12(packet.endsAt)}`
}

/** What the couple's choices can come to, with tax and service charge, and for a change still out, what's paid already. */
function RangeLine({ packet }: { packet: SalesPacketDto }) {
  const range = packetRange(
    packet.lines.map((l) => ({ ...l, key: l.id })),
    packet.rates,
  )
  const low = dollarsFromCents(range.low.totalCents)
  const high = dollarsFromCents(range.high.totalCents)
  const paid = packet.paidBeforeCents ?? 0
  if (packet.status === 'signed') {
    // Signed, it's what they chose: everything chosen counts, as if included.
    const agreed = packetRange(
      packet.lines.filter((l) => l.chosen).map((l) => ({ ...l, group: 'included' as const, key: l.id })),
      packet.rates,
    )
    return <p className="m-0 text-sm text-ink">Agreed at {dollarsFromCents(agreed.low.totalCents)}, with tax and service charge.</p>
  }
  return (
    <p className="m-0 text-sm text-ink">
      {low === high ? `Comes to ${low}` : `Comes to ${low} to ${high}, depending on what the couple picks`}, with tax and service charge.
      {paid > 0 ? ` ${dollarsFromCents(paid)} is paid already; its payments cover the rest.` : ''}
    </p>
  )
}

/**
 * The agreement as it stands: the booking's signed proposal, or the latest
 * signed change to it. Newest first, so the first one found.
 */
export function currentAgreement(sales: EventSalesDto): SalesPacketDto | undefined {
  return sales.packets.find((p) => isAgreementPacketKind(p.kind) && p.status === 'signed')
}

/** A change to the agreement that's still out: only one at a time. */
function changeOut(sales: EventSalesDto): SalesPacketDto | undefined {
  return sales.packets.find((p) => p.kind === 'amendment' && !p.lapsed && (p.status === 'open' || p.status === 'signing'))
}

function Lines({ packet }: { packet: SalesPacketDto }) {
  const groups = SALES_LINE_GROUPS.map((group) => ({ group, lines: packet.lines.filter((l) => l.group === group) }))
  return (
    <div className="space-y-3">
      {groups
        .filter((g) => g.lines.length > 0)
        .map(({ group, lines }) => (
          <div key={group} className="space-y-1">
            <h4 className="m-0 text-xs font-medium uppercase tracking-wide text-ink-muted">{GROUP_LABELS[group]}</h4>
            <ul className="m-0 list-none space-y-1 p-0">
              {lines.map((l) => (
                <li key={l.id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-ink">
                    {l.name}
                    {l.quantity > 1 ? <span className="text-ink-muted"> × {l.quantity}</span> : null}
                    {l.chosen && group !== 'included' ? <span className="text-ok-ink"> · chosen</span> : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink-muted">{dollarsFromCents(l.unitCents * l.quantity)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  )
}

interface PacketActionsProps {
  eventId: string
  packet: SalesPacketDto
  sales: EventSalesDto
}

/** Withdraw and extend: what staff can do with a proposal that's out or lapsed. */
function PacketActions({ eventId, packet }: Omit<PacketActionsProps, 'sales'>) {
  const change = packet.kind === 'amendment'
  const { toast } = useToast()
  const withdraw = useWithdrawPacket(eventId)
  const extend = useExtendPacket(eventId)
  const [confirming, setConfirming] = React.useState(false)
  const [extending, setExtending] = React.useState(false)
  const [until, setUntil] = React.useState<CalendarDay | ''>(packet.validUntil)
  const [error, setError] = React.useState<string | null>(null)

  const out = packet.status === 'open' || packet.status === 'signing'
  const reopenable = packet.status === 'expired' && packet.signers.every((s) => s.signedAt === null)
  if (!out && !reopenable) return null

  function handleExtend(): void {
    if (until === '') return setError('Choose the last day the couple can accept it.')
    setError(null)
    extend.mutate(
      { packetId: packet.id, validUntil: until },
      {
        onSuccess: () => {
          setExtending(false)
          const what = change ? 'Change' : 'Proposal'
          toast(packet.lapsed ? `${what} opened again.` : `${what} extended.`)
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <div className="space-y-2 border-t border-line pt-3">
      <div className="flex flex-wrap items-center gap-2">
        {extending ? (
          <>
            <DatePicker value={until} onChange={setUntil} min={todayIso()} max={packet.date} className="h-8" />
            <Button size="sm" onClick={handleExtend} disabled={extend.isPending}>
              {extend.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setExtending(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setExtending(true)}>
            {packet.lapsed ? 'Open again' : 'Extend'}
          </Button>
        )}
        {out ? (
          <Button size="sm" variant="outline" className="text-danger-ink hover:bg-danger-soft hover:text-danger-ink" onClick={() => setConfirming(true)}>
            Withdraw
          </Button>
        ) : null}
      </div>
      {error === null ? null : <FormError>{error}</FormError>}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={change ? 'Withdraw this change?' : 'Withdraw this proposal?'}
        description={
          change ? 'The couple can no longer sign it, and the agreement stays as it is.' : 'The couple can no longer accept it, and its date is free for another proposal or a booking.'
        }
        confirmLabel="Withdraw"
        destructive
        busy={withdraw.isPending}
        error={withdraw.error === null ? null : requestErrorMessage(withdraw.error)}
        onConfirm={() =>
          withdraw.mutate(packet.id, {
            onSuccess: () => {
              setConfirming(false)
              toast(change ? 'Change withdrawn.' : 'Proposal withdrawn.')
            },
          })
        }
      />
    </div>
  )
}

/** Who signs for the venue: changeable while the proposal is out and nobody has signed. */
function Countersigner({ eventId, packet, sales }: PacketActionsProps) {
  const { toast } = useToast()
  const change = useSetCountersigner(eventId)
  const changeable = sales.canEdit && packet.status === 'open' && !packet.lapsed && sales.countersigners.length > 1
  if (!changeable) return <>{packet.countersigner?.name ?? 'Nobody'}</>
  return (
    <span className="inline-flex flex-col gap-1">
      <Select
        size="sm"
        aria-label="Who countersigns"
        value={packet.countersigner?.userId ?? ''}
        disabled={change.isPending}
        onChange={(e) => change.mutate({ packetId: packet.id, countersignerId: e.target.value }, { onSuccess: () => toast('Countersigner changed.') })}
      >
        {packet.countersigner === null ? <option value="">Nobody</option> : null}
        {sales.countersigners.map((c) => (
          <option key={c.userId} value={c.userId}>
            {c.name}
          </option>
        ))}
      </Select>
      {change.error === null ? null : <span className="text-xs text-danger-ink">{requestErrorMessage(change.error)}</span>}
    </span>
  )
}

/** The countersigner's turn: open the contract once the couple have all signed. */
function Countersign({ eventId, packet }: Omit<PacketActionsProps, 'sales'>) {
  const change = packet.kind === 'amendment'
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const open = useCountersign(eventId)
  const [embedUrl, setEmbedUrl] = React.useState<string | null>(null)
  const refresh = React.useCallback(() => void queryClient.invalidateQueries({ queryKey: salesKeys.event(eventId) }), [eventId, queryClient])
  const handleOutcome = React.useCallback(
    (outcome: SigningOutcome) => {
      setEmbedUrl(null)
      refresh()
      if (outcome !== 'declined') toast(`Countersigned. The signed ${change ? 'change' : 'contract'} will be in Documents shortly.`)
    },
    [change, refresh, toast],
  )
  return (
    <div className="space-y-2">
      <Button size="sm" onClick={() => open.mutate(packet.id, { onSuccess: (link) => setEmbedUrl(link.embedUrl) })} disabled={open.isPending}>
        <PenLine className="h-4 w-4" />
        {open.isPending ? 'Opening…' : 'Countersign'}
      </Button>
      {open.error === null ? null : <FormError>{requestErrorMessage(open.error)}</FormError>}
      <SigningFrame
        embedUrl={embedUrl}
        title={change ? 'Countersign the change' : 'Countersign the contract'}
        description={change ? 'The couple have signed. Your signature completes the change to the agreement.' : 'The couple have signed. Your signature completes the agreement.'}
        onClose={() => {
          setEmbedUrl(null)
          refresh()
        }}
        onOutcome={handleOutcome}
      />
    </div>
  )
}

/** An invoice for extras: what's on it and what it comes to. Its payments are with the rest. */
function InvoiceCard({ packet }: { packet: SalesPacketDto }) {
  const total = packetRange(
    packet.lines.map((l) => ({ ...l, key: l.id })),
    packet.rates,
  ).low.totalCents
  return (
    <Card asChild className="space-y-3 p-4">
      <li>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="m-0 text-sm font-semibold text-ink">Invoice</h3>
            <p className="m-0 text-xs text-ink-muted">
              Sent {shortDay(packet.sentAt)}
              {packet.sentBy === null ? '' : ` by ${packet.sentBy}`}
            </p>
          </div>
          <Badge tone="neutral">Invoice</Badge>
        </div>
        <Lines packet={packet} />
        <p className="m-0 text-sm text-ink">Comes to {dollarsFromCents(total)}, with tax and service charge.</p>
      </li>
    </Card>
  )
}

/** After who sent it: " · open until Nov 4, 2026", " · signed Oct 22, 2026", " · withdrawn Oct 23, 2026". */
function metaOf(packet: SalesPacketDto, label: string, closed: string | null): string {
  if (packet.status === 'signed') return packet.countersignedAt === null ? '' : ` · signed ${shortDay(packet.countersignedAt)}`
  return closed === null ? ` · open until ${formatEventDate(packet.validUntil)}` : ` · ${label.toLowerCase()} ${closed}`
}

/** "Signed", "Lapsed", or "Changed" for an agreement a later signed change replaced. */
function labelOf(packet: SalesPacketDto, sales: EventSalesDto): string {
  if (packet.lapsed) return 'Lapsed'
  const replaced = sales.packets.some((p) => p.kind === 'amendment' && p.status === 'signed' && p.amendsPacketId === packet.id)
  return replaced ? 'Changed' : SALES_PACKET_STATUS_LABELS[packet.status]
}

function PacketCard({ eventId, packet, sales }: PacketActionsProps) {
  const { user } = useAuth()
  if (packet.kind === 'invoice') return <InvoiceCard packet={packet} />
  const label = labelOf(packet, sales)
  const closed = packet.closedAt === null ? null : shortDay(packet.closedAt)
  const title = packet.kind === 'amendment' ? `Change to the agreement, for ${whenOf(packet)}` : `Proposal for ${whenOf(packet)}`
  return (
    <Card asChild className="space-y-3 p-4">
      <li>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
            <p className="m-0 text-xs text-ink-muted">
              Sent {shortDay(packet.sentAt)}
              {packet.sentBy === null ? '' : ` by ${packet.sentBy}`}
              {metaOf(packet, label, closed)}
            </p>
          </div>
          <Badge tone={label === 'Changed' ? 'neutral' : statusTone(packet)}>{label}</Badge>
        </div>
        <Lines packet={packet} />
        <RangeLine packet={packet} />
        <dl className="m-0 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div className="flex gap-2">
            <dt className="text-ink-muted">Signs</dt>
            <dd className="m-0 flex flex-wrap gap-x-3 text-ink">
              {packet.signers.map((s) => (
                <span key={s.userId} className={cn('inline-flex items-center gap-1', s.signedAt !== null && 'text-ok-ink')}>
                  {s.signedAt === null ? null : <Check className="h-3.5 w-3.5" aria-label="signed" />}
                  {s.name}
                </span>
              ))}
            </dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-muted">Countersigns</dt>
            <dd className="m-0 text-ink">
              <Countersigner eventId={eventId} packet={packet} sales={sales} />
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-ink-muted">Contract</dt>
            <dd className="m-0 text-ink">{packet.templateName}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-ink-muted">Security deposit</dt>
            <dd className="m-0 text-ink">
              {packet.securityDeposit === null ? 'None' : `${dollarsFromCents(packet.securityDeposit.cents)}, ${dueText(packet.securityDeposit.due)}`}
            </dd>
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <dt className="shrink-0 text-ink-muted">Payments</dt>
            <dd className="m-0 text-ink">{packet.schedule.map(paymentText).join(' · ')}</dd>
          </div>
        </dl>
        {packet.countersignReady && packet.countersigner?.userId === user?.id ? <Countersign eventId={eventId} packet={packet} /> : null}
        {packet.countersignReady && packet.countersigner?.userId !== user?.id ? (
          <p className="m-0 text-sm text-ink-muted">The couple have signed. {packet.countersigner?.name ?? 'The countersigner'} countersigns next.</p>
        ) : null}
        {sales.canEdit ? <PacketActions eventId={eventId} packet={packet} /> : null}
      </li>
    </Card>
  )
}

/** Why there's no "Send a proposal" button, or null when there is one. */
function sendBlocker(event: EventDetailDto, sales: EventSalesDto, readiness: SalesReadinessDto): React.ReactNode {
  // A booking's proposal has done its job; one with none was booked by hand.
  if (event.lifecycle !== 'enquiry') return sales.packets.length > 0 ? <></> : <p className="m-0 text-sm text-ink-muted">Proposals are sent to inquiries.</p>
  const missing = SETUP_LINKS.filter((l) => !readiness[l.flag])
  if (missing.length > 0) {
    return (
      <Callout tone="neutral" title="Before this venue can send a proposal">
        <ul className="m-0 list-disc space-y-0.5 pl-5">
          {missing.map((m) => (
            <li key={m.flag}>
              <Link className="underline" to={`/venues/${event.venueId}/settings#${m.section}`}>
                {m.label}
              </Link>
            </li>
          ))}
        </ul>
      </Callout>
    )
  }
  if (!readiness.sendingOpen) return <p className="m-0 text-sm text-ink-muted">Sending proposals opens soon.</p>
  const holding = sales.packets.some((p) => p.kind === 'booking' && !p.lapsed && (p.status === 'open' || p.status === 'signing' || p.status === 'signed'))
  if (sales.packets.some((p) => p.kind === 'booking' && p.status === 'signed')) {
    return <p className="m-0 text-sm text-ink-muted">The proposal is signed. The event books itself once the deposit is paid.</p>
  }
  if (holding) return <p className="m-0 text-sm text-ink-muted">This inquiry has a proposal out. Withdraw it, or wait for it to lapse, to send another.</p>
  return null
}

/**
 * An event's Sales section (docs/sales.spec.md, "Sending a packet"): the
 * proposals it was sent, and sending one. Sales View sees it; Sales Edit sends.
 */
export function EventSalesSection({ event }: { event: EventDetailDto }) {
  const sales = useEventSales(event.id)
  const readiness = useSalesReadiness(event.venueId)
  const [building, setBuilding] = React.useState(false)
  const [invoicing, setInvoicing] = React.useState(false)
  const [changing, setChanging] = React.useState(false)

  if (sales.isPending || readiness.isPending) return <DetailSkeleton />
  if (sales.isError) return <ErrorState message={requestErrorMessage(sales.error)} onRetry={() => void sales.refetch()} />
  if (readiness.isError) return <ErrorState message={requestErrorMessage(readiness.error)} onRetry={() => void readiness.refetch()} />

  const blocker = sales.data.canEdit ? sendBlocker(event, sales.data, readiness.data) : null
  const agreement = currentAgreement(sales.data)
  const ready = readiness.data.sendingOpen && SETUP_LINKS.every((l) => readiness.data[l.flag])
  const canChange = sales.data.canEdit && event.lifecycle === 'booked' && ready && agreement !== undefined
  return (
    <div className="space-y-4">
      {sales.data.canEdit && blocker === null ? (
        <Button onClick={() => setBuilding(true)}>
          <Send className="h-4 w-4" />
          Send a proposal
        </Button>
      ) : (
        blocker
      )}
      <div className="flex flex-wrap gap-2">
        {canChange && changeOut(sales.data) === undefined ? (
          <Button variant="outline" onClick={() => setChanging(true)}>
            <FilePen className="h-4 w-4" />
            Change the agreement
          </Button>
        ) : null}
        {sales.data.canEdit && event.lifecycle === 'booked' && readiness.data.sendingOpen && readiness.data.business ? (
          <Button variant="outline" onClick={() => setInvoicing(true)}>
            <Receipt className="h-4 w-4" />
            Send an invoice
          </Button>
        ) : null}
      </div>
      {canChange && changeOut(sales.data) !== undefined ? (
        <p className="m-0 text-sm text-ink-muted">A change to the agreement is out. Withdraw it, or wait for it to be signed or lapse, to send another.</p>
      ) : null}
      <PaymentsPanel eventId={event.id} sales={sales.data} />
      {sales.data.packets.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">No proposals yet.</p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {sales.data.packets.map((p) => (
            <PacketCard key={p.id} eventId={event.id} packet={p} sales={sales.data} />
          ))}
        </ul>
      )}
      {building ? <PacketBuilder event={event} sales={sales.data} open={building} onOpenChange={setBuilding} /> : null}
      {invoicing ? <InvoiceBuilder event={event} sales={sales.data} open={invoicing} onOpenChange={setInvoicing} /> : null}
      {changing && agreement !== undefined ? <PacketBuilder event={event} sales={sales.data} amends={agreement} open={changing} onOpenChange={setChanging} /> : null}
    </div>
  )
}
